import React from 'react';
import { RiskFactorsProps } from '../../../types/health-tools/predictive-types';
import HealthCard from '../ui/HealthCard';
import { Activity, TrendingUp, TrendingDown } from 'lucide-react';

/**
 * Risk Factors Component
 * 
 * Displays a user's health risk factors with scores and trends
 * 
 * @param riskFactors - Array of risk factor objects with name, score, and trend
 * @param className - Additional CSS classes
 */
const RiskFactors: React.FC<RiskFactorsProps> = ({
  riskFactors,
  className = '',
}) => {
  // Get status color based on score
  const getScoreColor = (score: number) => {
    if (score < 30) return 'var(--status-success)';
    if (score < 60) return 'var(--status-warning)';
    return 'var(--status-error)';
  };
  
  // Get trend display properties
  const getTrendInfo = (trend: string) => {
    switch(trend) {
      case 'improving':
        return {
          icon: TrendingDown,
          label: 'Improving',
          bgColor: 'var(--status-success-subtle)',
          textColor: 'var(--status-success)'
        };
      case 'declining':
        return {
          icon: TrendingUp,
          label: 'Declining',
          bgColor: 'var(--status-error-subtle)',
          textColor: 'var(--status-error)'
        };
      case 'stable':
      default:
        return {
          icon: TrendingDown,
          label: 'Stable',
          bgColor: 'var(--status-warning-subtle)',
          textColor: 'var(--status-warning)',
          rotate: 'rotate-90'
        };
    }
  };
  
  return (
    <HealthCard 
      title="Health Risk Factors" 
      icon={Activity} 
      className={className}
    >
      <div className="space-y-4">
        {riskFactors.map((factor, index) => {
          const trendInfo = getTrendInfo(factor.trend);
          const TrendIcon = trendInfo.icon;
          
          return (
            <div key={index} className="space-y-1">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-[var(--text-primary)]">
                  {factor.name}
                </span>
                <div 
                  className="flex items-center text-xs px-2 py-0.5 rounded"
                  style={{ 
                    backgroundColor: trendInfo.bgColor,
                    color: trendInfo.textColor
                  }}
                >
                  <TrendIcon 
                    className={`w-3 h-3 mr-1 ${trendInfo.rotate || ''}`} 
                  />
                  {trendInfo.label}
                </div>
              </div>
              
              <div className="w-full h-2 bg-[var(--background-inset)] rounded-full">
                <div 
                  className="h-full rounded-full" 
                  style={{ 
                    width: `${factor.score}%`,
                    backgroundColor: getScoreColor(factor.score)
                  }}
                ></div>
              </div>
              
              <div className="text-right text-xs text-[var(--text-tertiary)] mt-0.5">
                Score: {factor.score}
              </div>
            </div>
          );
        })}
        
        {riskFactors.length === 0 && (
          <p className="text-center text-[var(--text-secondary)] py-4">
            No risk factors data available.
          </p>
        )}
      </div>
      
      <p className="text-xs text-[var(--text-secondary)] mt-4">
        Risk factors are calculated based on your health data and lifestyle information. 
        Lower scores indicate lower risk.
      </p>
    </HealthCard>
  );
};

export default RiskFactors; 